/**
 * StudioWindow.jsx — the 480×420 studio window, rendered on the website.
 *
 * The same chrome the extension draws over Gmail's compose box (content-script
 * pa-sw-*), here as React:
 *   • <StudioWindow/> — the staged window (moment → voice → draft → inserted),
 *     as a STATIC frame on landing pages and sign-in.
 *   • <LightStudioWindow/> — the light (E3) window the home page plays: draft,
 *     voice rail, Me chips, Length / Formality dials, surface tag.
 */
import { PMark, VOICES } from './SiteShell.jsx';
import { DialRows, LEVELS, stopOfWord } from './DialTrack.jsx';

export const GABBI = {
  who: 'Gabbi',
  brief: ['yes to Friday', 'budget left out'],
  draft: "Hi Gabbi, Friday works — go ahead and confirm with engineering. Let's keep the budget figure out of the deck for now; I'll send it separately.",
  message: 'Does Friday still work for the planning session? Also wondering whether we should put the budget line in the deck.',
};

function WindowChrome({ title, step = 1, back = true, fwd = false, dock = false, children }) {
  return (
    <div className={`w${dock ? ' dock' : ''}`}>
      <div className="w-grab" />
      <div className="w-head">
        <span className={`w-nav${back ? '' : ' off'}`}>‹</span>
        <span className={`w-nav${fwd ? '' : ' off'}`}>›</span>
        <span className="w-title">{title}</span>
        <span className="w-steps">{[0, 1, 2].map((i) => <i key={i} className={i === step ? 'on' : undefined} />)}</span>
        <span className="w-av">A</span>
        <span className="w-x">✕</span>
      </div>
      {children}
    </div>
  );
}

export function WindowVoiceStrip({ selected = 'me' }) {
  return (
    <div className="w-vstrip">
      <div className="w-vline" />
      <div className="w-vrow">
        {VOICES.map((v) => (
          <div className={`w-vitem${v.id === selected ? ' on' : ''}`} key={v.id} style={{ color: v.color }}>
            <i style={{ background: v.color }} />
            <span>{v.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Brief({ who, chips, lead }) {
  return (
    <div className="w-brief">{lead || `A reply to ${who}`} · {chips.map((c, i) => <span key={c}>{i > 0 && ' · '}<span className="u">{c}</span></span>)}</div>
  );
}

/** One stage of the window. `mode`: 'moment' | 'voice' | 'writing' | 'draft' | 'inserted' | 'lock' */
export function StudioWindow({ mode = 'voice', voice = 'me', data = GABBI, dock = false, length = 'Short', formality = 'Polished', lock = null, animate = true }) {
  const v = VOICES.find((x) => x.id === voice) || VOICES[0];
  // Design 9b: Length / Formality as four-stop hairlines, the voice's own
  // levels (no override in a demo, so no orange).
  const lenStop = stopOfWord('len', length) ?? 2;
  const formStop = stopOfWord('form', formality) ?? 0;
  const body = (() => {
    if (mode === 'lock') {
      return (
        <WindowChrome title="PERSONADRAFT" step={0} back={false} dock={dock}>
          <div className="w-lock">
            <PMark size={40} />
            <b>{lock?.title || 'Sign in to unlock this window'}</b>
            <p>{lock?.sub || 'Draft a few emails here, free. Your 30-day unlimited trial starts when you install.'}</p>
            {lock?.action}
          </div>
        </WindowChrome>
      );
    }
    if (mode === 'moment') {
      return (
        <WindowChrome title={`REPLY · ${data.who.toUpperCase()}`} step={0} back={false} dock={dock}>
          <div className="w-body">
            <div className="w-brief">{data.who} asked whether Friday works, and whether the budget goes in the deck.</div>
            <div className="w-label">THE MOMENT</div>
            <div className="w-brief" style={{ marginTop: 6 }}>A reply to {data.who} · <span className="u">yes to Friday</span> · <span className="u">not yet</span> · <span className="u">budget left out</span> · <span className="u">include it</span></div>
            <div className="w-foot"><span>Answer the two that matter. Nothing is decided for you.</span><span className="w-go">Next</span></div>
          </div>
        </WindowChrome>
      );
    }
    if (mode === 'voice') {
      return (
        <WindowChrome title={`REPLY · ${data.who.toUpperCase()}`} step={1} dock={dock}>
          <div className="w-body">
            <Brief who={data.who} chips={data.brief} lead={data.lead} />
            <div className="w-label">VOICE</div>
            <WindowVoiceStrip selected={voice} />
            <DialRows length={lenStop} formality={formStop} style={{ marginTop: 18 }} />
            <div className="w-foot"><span>Me never changes. The moment only sets guardrails.</span><span className="w-go">Draft it</span></div>
          </div>
        </WindowChrome>
      );
    }
    if (mode === 'writing') {
      return (
        <WindowChrome title="WRITING" step={1} dock={dock}>
          <div className="w-body">
            <div className="w-brief">{data.brief.map((c, i) => <span key={c}>{i > 0 && ' · '}<b>{c}</b></span>)} · in <b>{v.name}</b></div>
            <div className="w-skel"><i style={{ width: '34%' }} /><i style={{ width: '88%' }} /><i style={{ width: '72%' }} /><i style={{ width: '24%', animationDelay: '.25s' }} /></div>
            <div className="w-foot"><span>Reading the thread, matching the voice…</span><span className="w-go" style={{ opacity: .4 }}>Insert</span></div>
          </div>
        </WindowChrome>
      );
    }
    const inserted = mode === 'inserted';
    return (
      <WindowChrome title={<>DRAFT 1 / 1 · <span style={{ color: v.color }}>{v.name.toUpperCase()}</span></>} step={2} fwd={false} dock={dock}>
        <div className="w-body tight">
          <Brief who={data.who} chips={data.brief} lead={data.lead} />
          <div className="w-well">{data.draft}</div>
          <DialRows length={lenStop} formality={formStop} style={{ marginTop: 10 }} />
          <div className="w-drow">
            <span className={`w-go${inserted ? ' done' : ''}`} style={{ marginLeft: 'auto' }}>{inserted ? 'Inserted ✓' : 'Insert'}</span>
          </div>
        </div>
      </WindowChrome>
    );
  })();
  return animate ? <div className="w-in" key={mode}>{body}</div> : body;
}

/* ── The light window (E3) ────────────────────────────────────────────────
   The window as it opens on every surface: a draft, the voice rail, the Me
   chips and the two register dials. Used by the home hero, Control and Every
   Surface sections. Interactive only where a handler is passed.

   Dial grammar (shape carries the state):
     dashed ring — the value as saved      ink dot (8px)   — the current value
     orange dot (10px) — changed by you    white dot, orange border (12px) — changed by
                                           the surface: word in orange-deep + surface tag */

const VARIANTS = [['me', 'Me'], ['warm', 'Me, warmer'], ['firm', 'Me, firmer'], ['fast', 'Me, faster']];
const LEN_WORDS = LEVELS.len.stops.map((s) => s.w);
const FORM_WORDS = LEVELS.form.stops.map((s) => s.w);
const STOP_GAP = 38;

/** VOICES carry the engine ids; the light window speaks in single-word keys ('me', 'direct', …). */
export const voiceKey = (v) => v.name.toLowerCase();

function LightDial({ label, words, value, saved, by, tag }) {
  const fromSurface = by === 'surface';
  return (
    <div className="lw-dial">
      <span className="lw-dial-k">{label}</span>
      <span className="lw-track" aria-hidden="true">
        {words.map((w, i) => (
          <span key={w} className="lw-stop" style={{ left: 6 + i * STOP_GAP }}>
            {i === saved && <i className="lw-ring" />}
            <i className={`lw-dot${i === value ? ` ${by === 'user' || fromSurface ? by : 'cur'}` : ''}`} />
          </span>
        ))}
      </span>
      <span className={`lw-dial-w${fromSurface ? ' surface' : ''}`}>{words[value]}</span>
      {fromSurface && tag && <span className="lw-dial-tag">{tag}</span>}
    </div>
  );
}

export function LightStudioWindow({
  label = 'DRAFT 1 / 1 · ME', metaLead = 'A reply to Gabbi', metaFraming = 'tell them it’s moved', metaRead = 'read 3 messages',
  draft = "Gabbi — Friday's off. The vendor fell through this afternoon, so I'm pushing the launch. New date by Tuesday.\n— M",
  voice = 'me', variant = 'me', surface = '',
  lenIdx = 1, lenBy = 'saved', formIdx = 2, formBy = 'saved', savedLen = 1, savedForm = 2,
  busy = false, onVoice, onVariant, onInsert,
}) {
  const current = VOICES.find((v) => voiceKey(v) === voice) || VOICES[0];
  const isMe = voiceKey(current) === 'me';
  const Voice = onVoice ? 'button' : 'span';
  const Chip = onVariant ? 'button' : 'span';
  return (
    <div className="lw">
      <div className="lw-grab" />
      <div className="lw-head">
        <span className="lw-navs" aria-hidden="true"><span>‹</span><span>›</span></span>
        <span className="lw-label">{label}</span>
        <span className="lw-pages" aria-hidden="true"><i /><i className="on" /><i /></span>
        <span className="lw-av" aria-hidden="true">M</span>
        <span className="lw-x" aria-hidden="true">✕</span>
      </div>
      <div className="lw-body">
        <div className="lw-meta">{metaLead} · <b>{metaFraming}</b> · <u>{metaRead}</u></div>
        <div className="lw-draft" key={draft}>
          {draft}
          {busy && <div className="lw-busy"><i />RE-DRAFTING</div>}
        </div>
        <div className="lw-rail">
          <div className="lw-rail-row" role={onVoice ? 'group' : undefined} aria-label={onVoice ? 'Voice for this draft' : undefined}>
            {VOICES.map((v) => {
              const on = v.id === current.id;
              const key = voiceKey(v);
              return (
                <Voice key={v.id} className={`lw-v${on ? ' on' : ''}`} title={v.desc}
                  {...(onVoice ? { type: 'button', 'aria-pressed': on, 'aria-label': `${v.name}, ${v.desc}`, onClick: () => onVoice(key) } : {})}>
                  <i style={{ background: v.color, boxShadow: on ? `0 0 0 3px #fff, 0 0 0 4px ${key === 'me' ? 'rgba(15,20,32,.25)' : v.color}` : 'none' }} />
                  <span>{v.name}</span>
                </Voice>
              );
            })}
          </div>
        </div>
        <div className="lw-tune">
          {isMe ? (
            <div className="lw-chips" role={onVariant ? 'group' : undefined} aria-label={onVariant ? 'Nudge your own voice' : undefined}>
              {VARIANTS.map(([id, l]) => (
                <Chip key={id} className={`lw-chip${id === variant ? ' on' : ''}`}
                  {...(onVariant ? { type: 'button', 'aria-pressed': id === variant, onClick: () => onVariant(id) } : {})}>{l}</Chip>
              ))}
            </div>
          ) : (
            <div className="lw-note">Writing as <b>{current.name}</b> for this draft · Me is unchanged</div>
          )}
        </div>
        <div className="lw-dials">
          <LightDial label="Length" words={LEN_WORDS} value={lenIdx} saved={savedLen} by={lenBy} tag={surface} />
          <LightDial label="Formality" words={FORM_WORDS} value={formIdx} saved={savedForm} by={formBy} tag={surface} />
        </div>
      </div>
      <div className="lw-foot">
        {onInsert ? <button type="button" className="lw-insert" onClick={onInsert}>Insert</button> : <span className="lw-insert">Insert</span>}
        <span className="lw-undo">↶ undo after insert</span>
        <span className="lw-fine">Fine-tune ›</span>
      </div>
    </div>
  );
}

/** Static window inside the cool backdrop (landing heroes). */
export function StaticDemo({ mode = 'draft', voice = 'me', data = GABBI, length, formality }) {
  return (
    <div className="s-demo">
      <StudioWindow mode={mode} voice={voice} data={data} length={length} formality={formality} animate={false} />
    </div>
  );
}
